import React, {useCallback, useState} from 'react'
import {useDropzone} from 'react-dropzone'
import Cropper from 'react-easy-crop'
import {Area, MediaSize} from 'react-easy-crop/types'
import {FormattedMessage} from 'react-intl'
import {captureException} from '../../../errors'
import Dialog from '../../Dialog'
import FlatButton from '../../FlatButton'
import StyledButton from '../../StyledButton'
import Toast from '../../Toast'
import DeleteIcon from '../assets/DeleteIcon'
import DeleteConfirmationPopup from './DeleteConfirmationPopup'
import {getCroppedImg} from './canvasUtils'

type Props = Readonly<{
  onImageSelect: (image: File) => void
  onImageRemove: () => void
  currentImageUrl?: string | null
}>

const minZoom = 0.5

function InlineImageUpload({
  onImageSelect,
  onImageRemove,
  currentImageUrl
}: Props) {
  const [showImageCropper, setShowImageCropper] = useState(false)
  const [crop, setCrop] = useState({x: 0, y: 0})
  const [zoom, setZoom] = useState(minZoom)
  const [imgSrc, setImgSrc] = useState<string | undefined>(undefined)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [fileSizeError, setFileSizeError] = useState(false)
  const [imageSizeWarning, setImageSizeWarning] = useState(false)
  const [showDeleteConfirmationPopup, setShowDeleteConfirmationPopup] =
    useState(false)
  const [imageMarkedForRemoval, setImageMarkedForRemoval] = useState(false)

  function readFile(file: File): Promise<string | ArrayBuffer | null> {
    return new Promise(resolve => {
      const reader = new FileReader()
      reader.addEventListener('load', () => resolve(reader.result), false)
      reader.readAsDataURL(file)
    })
  }

  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png']
    },
    multiple: false,
    onDrop: async acceptedFiles => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0]
        const fileSize = file.size / 1024 / 1024

        if (fileSize >= 1) {
          setFileSizeError(true)
          return
        }

        setFileSizeError(false)
        setImageFile(file)

        const imageDataUrl = await readFile(file)
        if (imageDataUrl) {
          setImgSrc(imageDataUrl.toString())
          setShowImageCropper(true)
        }
      }
    }
  })

  async function handleBrowseClick() {
    // Trigger the file input
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/jpeg,image/jpg,image/png'
    input.onchange = async e => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        const fileSize = file.size / 1024 / 1024

        if (fileSize >= 1) {
          setFileSizeError(true)
          return
        }

        setFileSizeError(false)
        setImageFile(file)

        const imageDataUrl = await readFile(file)
        if (imageDataUrl) {
          setImgSrc(imageDataUrl.toString())
          setShowImageCropper(true)
        }
      }
    }
    input.click()
  }

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels)
    },
    []
  )

  function onMediaLoaded(media: MediaSize) {
    setImageSizeWarning(media.naturalWidth < 693 || media.naturalHeight < 390)
  }

  const showCroppedImage = useCallback(
    async function () {
      try {
        if (imgSrc && croppedAreaPixels) {
          const croppedImage = await getCroppedImg(
            imgSrc,
            croppedAreaPixels,
            imageFile?.name || 'sponsor_image',
            imageFile?.type || 'image/png'
          )

          if (croppedImage) {
            onImageSelect(croppedImage)
            setImageMarkedForRemoval(false)
            setShowImageCropper(false)
          }
          setCrop({x: 0, y: 0})
          setZoom(minZoom)
        }
      } catch (e) {
        captureException(e)
      }
    },
    [imgSrc, croppedAreaPixels, imageFile, onImageSelect]
  )

  function handleCropCancel() {
    setShowImageCropper(false)
    setImgSrc(undefined)
    setImageFile(null)
    setFileSizeError(false)
    setImageSizeWarning(false)
    setCrop({x: 0, y: 0})
    setZoom(minZoom)
  }

  function handleRemoveImage() {
    setShowDeleteConfirmationPopup(true)
  }

  function confirmDeleteImage() {
    setImageMarkedForRemoval(true)
    onImageRemove()
    setShowDeleteConfirmationPopup(false)
  }

  return (
    <div className="w-[60%]">
      {/* Current Image Display */}
      {currentImageUrl && !imageMarkedForRemoval && (
        <div className="mb-4">
          <div className="border-gray-300 bg-gray-50 relative flex h-48 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors">
            <img
              src={currentImageUrl}
              alt="Current sponsor image"
              className="border-gray-300 aspect-video h-32 rounded-lg border object-contain"
            />
            <div
              className="absolute -right-1 -top-1 z-10"
              onClick={handleRemoveImage}
            >
              <DeleteIcon />
            </div>
          </div>
        </div>
      )}

      {/* Drag and Drop Area */}
      {(!currentImageUrl || imageMarkedForRemoval) && (
        <div className="w-full">
          <div
            {...getRootProps()}
            className={`border-gray-300 bg-gray-50 flex h-48 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors ${isDragActive ? 'border-teal-500 bg-teal-50' : 'hover:border-gray-400 hover:bg-gray-100'} `}
          >
            <input {...getInputProps()} />

            {/* Drag and Drop Text */}
            <p className="text-gray-600 mb-2 text-center">
              <FormattedMessage id="manageCommunity.image_drag" />
            </p>

            {/* Or Text */}
            <p className="text-gray-500 mb-4 text-center">
              <FormattedMessage id="manageCommunity.or" />
            </p>

            {/* Browse Button */}
            <FlatButton
              variant="secondary"
              width="full"
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                handleBrowseClick()
              }}
            >
              <FormattedMessage id="manageCommunity.browse" />
            </FlatButton>
          </div>
          {fileSizeError && (
            <div className="mb-4 w-full [&>div>div]:!w-full [&>div]:!w-full">
              <Toast variant="error">
                <FormattedMessage id="manageCommunity.file_size_warning" />
              </Toast>
            </div>
          )}
        </div>
      )}

      {/* Image Cropper Modal */}
      <Dialog open={showImageCropper} onClose={handleCropCancel}>
        <div className="m-auto flex max-w-[80%] flex-col items-start space-y-4">
          <Dialog.Title className="typography-headline-5 mx-auto text-teal">
            <FormattedMessage id="manageCommunity.upload_image" />
          </Dialog.Title>
          {fileSizeError && (
            <div className="w-full">
              <FormattedMessage id="manageCommunity.file_size_warning" />
            </div>
          )}
          {imageSizeWarning && (
            <div className="size-full text-center">
              <FormattedMessage id="manageCommunity.image_size_warning" />
            </div>
          )}

          {imgSrc && (
            <>
              <div className="relative m-auto h-96 max-h-[50vh] w-full">
                <Cropper
                  image={imgSrc}
                  crop={crop}
                  zoom={zoom}
                  minZoom={minZoom}
                  aspect={16 / 9}
                  onCropChange={setCrop}
                  restrictPosition={false}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  onMediaLoaded={onMediaLoaded}
                  zoomSpeed={0.1}
                />
              </div>
              <input
                type="range"
                className="h-1.5 w-full cursor-pointer appearance-none rounded-lg border-transparent bg-teal"
                value={zoom}
                min={minZoom}
                max={3}
                step={0.1}
                onChange={({target: {value}}) => setZoom(Number(value))}
              />
              <div className="ml-auto flex w-full justify-between">
                <StyledButton onClick={handleCropCancel}>
                  <FormattedMessage id="learnerCreator.topbar_button_cancel" />
                </StyledButton>
                <StyledButton onClick={showCroppedImage} disabled={false}>
                  <FormattedMessage id="manageCommunity.crop" />
                </StyledButton>
              </div>
            </>
          )}
        </div>
      </Dialog>

      <DeleteConfirmationPopup
        open={showDeleteConfirmationPopup}
        onAccept={confirmDeleteImage}
        onCancel={() => setShowDeleteConfirmationPopup(false)}
      />
    </div>
  )
}

export default InlineImageUpload
